import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { orderService, userService } from 'services';
import { _Object, OrderType } from 'utils/interfaces';
import { strings, doAmountFormat, handleFilters, getSortIcon, routes, serialNumber, doDateFormat, doTimeFormat, printReceipt, capitalize } from 'utils';
import { SearchField, LinkButton, Pagination, Breadcrumbs, ActiveFilters, SEOHeader, ReportExport, ReactDatePicker } from 'views/components';
import { AsyncSelectField, Button, SelectField } from 'views/components/form-inputs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import PrinterAccess from '../sales/printer-access';

const OrdersList = () => {
  const { t } = useTranslation()
  const selectAsyncSelect: any = useRef([])

  const { me, printerDevice } = useSelector((state: _Object) => state.session)

  const [loading, setLoading] = useState<_Object>(
    {
      listing: false,
      edit: false,
      update: false,
      pdf: false,

    });
  const [orders, setOrders] = useState<_Object>([])
  const [filterdLabel, setFilteredLabel] = useState<_Object>({})
  const [customers, setCustomers] = useState<_Object>([])
  const [filterApply, setFilterApply] = useState(false)

  const [filterData, setFilterData] = useState<_Object>({
    page: 1,
    per_page: process.env.REACT_APP_PER_PAGE,
    sort: '-created_at',
    start_date: '',
    end_date: '',
    filter: {
      customer_id: '',
      payment_status: '',
    }
  })

  const handleCallApi = () => {
    setLoading({ listing: true });
    orderService.getOrders({ q: formik.values.q, ...filterData }).then((data: _Object) => {
      if (data?.items) {
        setOrders(data)
        setLoading({ listing: false });
      }
    })
  }

  const formik = useFormik({
    initialValues: { q: '' },
    enableReinitialize: true,
    onSubmit: () => {
      if (formik.values.q.trim() !== '') {
        handleCallApi()
      }
    },
  });

  useEffect(() => {
    if (formik.values.q == '') {
      handleCallApi()
    }
    userService.getCustomerSearch('').then((data: _Object) => {
      setCustomers(data.items)
    })
  }, [filterApply, formik.values.q])

  let getTotalPrice = 0;
  let getDiscountPrice = 0;
  orders?.items?.filter((item: _Object) => {
    getTotalPrice = item.total + getTotalPrice
    getDiscountPrice = item.discount + getDiscountPrice
  })

  const [itemID] = useState('')

  const getOrderDetails = (id: string) => {
    if (id) {
      setLoading({ listing: true })
      orderService.getOrderDetails(id).then((data: OrderType) => {
        if (data) {
          printReceipt(printerDevice, data, me)
          setLoading({ listing: false })
        }
      })
    }
  }

  const handleDelete = async (orderId: string) => {
    const isConfirmed = window.confirm(
      'Are you sure you want to delete this order?'
    )

    if (isConfirmed) {
      const res = await orderService.deleteOrder(orderId)

      if (res.error === false) {
        setLoading({ listing: true });
        orderService.getOrders({ q: formik.values.q, ...filterData }).then((data: _Object) => {
          if (data?.items) {
            setOrders(data)
            setLoading({ listing: false });
          }
        })
      }
    } else {
      console.log('Delete cancelled')
    }
  }

  const loadOption = async (keywords: string) => {
    const data: _Object = await userService.getCustomerSearch({ q: keywords })
    return data.items?.map((item: _Object) => { return { label: item?.full_name != '' ? capitalize(item.full_name) : item.mobile_number, value: item.id } })
  }

  return (
    <>
      <SEOHeader title="Orders" />
      <Breadcrumbs
        trails={[
          {
            label: t('order.orders'),
            path: ''
          }
        ]}
      />

      <div className="pos-justify pos-between pos-align pos-center mt-3">
        <div className="d-flex header-loading relative">
          <form onSubmit={formik.handleSubmit}>
            <SearchField
              type="search"
              name="q"
              autoFocus
              disabled={!formik.values.q}
              className="mb-md-0"
              value={formik.values.q}
              onChange={(e: _Object) => {
                formik.setFieldValue('q', e.target.value)
              }}
            />
          </form>
          <button className="btn btn-secondary p-2 px-2.5 ms-2" type="button" data-bs-toggle="offcanvas" data-bs-target="#Orderlist" aria-controls="offcanvasRight">
            <svg className="mx-0" xmlns="http://www.w3.org/2000/svg" version="1.1" width="20" height="20" x="0" y="0" viewBox="0 0 512 512"><g><path d="M16 133.612h260.513c7.186 29.034 33.45 50.627 64.673 50.627s57.487-21.593 64.673-50.627H496c8.836 0 16-7.164 16-16s-7.164-16-16-16h-90.142c-7.185-29.034-33.449-50.628-64.673-50.628s-57.488 21.594-64.673 50.628H16c-8.836 0-16 7.164-16 16s7.164 16 16 16zm325.186-50.628c19.094 0 34.628 15.534 34.628 34.627 0 19.094-15.534 34.628-34.628 34.628s-34.628-15.534-34.628-34.628c0-19.093 15.534-34.627 34.628-34.627zM16 272h90.142c7.186 29.034 33.449 50.627 64.673 50.627s57.487-21.593 64.673-50.627H496c8.836 0 16-7.164 16-16s-7.164-16-16-16H235.487c-7.186-29.034-33.449-50.628-64.673-50.628S113.327 210.966 106.141 240H16c-8.836 0-16 7.164-16 16s7.163 16 16 16zm154.814-50.628c19.094 0 34.628 15.534 34.628 34.628 0 19.093-15.534 34.627-34.628 34.627S136.186 275.093 136.186 256c0-19.094 15.534-34.628 34.628-34.628zM496 378.388h-90.142c-7.186-29.034-33.449-50.628-64.673-50.628s-57.487 21.594-64.673 50.628H16c-8.836 0-16 7.164-16 16s7.164 16 16 16h260.513c7.186 29.034 33.449 50.628 64.673 50.628s57.487-21.594 64.673-50.628H496c8.836 0 16-7.164 16-16s-7.163-16-16-16zm-154.814 50.628c-19.094 0-34.628-15.534-34.628-34.628s15.534-34.628 34.628-34.628 34.628 15.534 34.628 34.628-15.534 34.628-34.628 34.628z" fill="#fff" opacity="1" data-original="#fff"></path></g></svg>
          </button>
          <div className="d-flex align-items-center order-button-list ms-3">
            {Object.values(filterData).some(value => !!value) && (
              <ActiveFilters filterdLabel={filterdLabel} setFilteredLabel={setFilteredLabel} setFilterData={setFilterData} filterData={filterData} setFilterApply={setFilterApply} />
            )}
          </div>

          <span className={`ms-4 ${loading.listing === true ? 'is-loading' : ''}`} />
        </div>
        <span className="d-flex align-items-center gap-3">
          <LinkButton className="primary" path={`${routes.sales}`} label="">
            <i className="fa-solid fa-plus text-white"></i>
            {t('order.add')}
          </LinkButton>
        </span>
      </div>
      <div className="card border-0 my-3 position-relative">
        <div className="table-responsive-md">
          <table
            className="table table-borderless table-striped order-listing w-100"
            style={{ minWidth: '900px' }}
          >
            <thead className="bg-white sticky-top">
              <tr>
                <th>#</th>
                <th>
                  <button
                    className="btn p-0 border-0"
                    onClick={() => { handleFilters('sort', filterData.sort === '-order_number' ? 'order_number' : '-order_number', setFilterData) }}>
                    {t('Order no')}
                    {getSortIcon(filterData.sort.includes('order_number') ? filterData.sort : '')}
                  </button>
                </th>

                <th>
                  {t('customer.customer')}
                </th>

                <th>
                  {t('orderDetails.payment_status')}</th>
                <th>{t('common.total')}</th>

                <th>
                  <button
                    className="btn p-0 border-0"
                    onClick={() => { handleFilters('sort', filterData.sort === '-created_at' ? 'created_at' : '-created_at', setFilterData) }}>
                    {t('common.createDate')}
                    {getSortIcon(filterData.sort.includes('created_at') ? filterData.sort : '')}
                  </button>
                </th>

                <th>
                  <button
                    className="btn p-0 border-0"
                    onClick={() => { handleFilters('sort', filterData.sort === '-updated_at' ? 'updated_at' : '-updated_at', setFilterData) }}>
                    {t('common.updateDate')}
                    {getSortIcon(filterData.sort.includes('updated_at') ? filterData.sort : '')}
                  </button>
                </th>
                <th className="action">{t('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {orders?.items?.map((item: _Object, i: number) => {
                return (
                  <tr key={item.order_number}>
                    <td>{serialNumber(filterData.per_page, orders?.pagination?.current_page, i)}</td>
                    <td>{item.order_number}</td>
                    <td>
                      <span>
                        {item?.customer?.full_name ? item?.customer?.full_name : '-'}<br></br>
                        <small>{item?.customer?.mobile_number}</small>
                      </span>
                    </td>
                    <td>{item?.status ? t(`order.${item.payment_status === null ? 'paid' : item.payment_status}`) : '-'}</td>

                    <td>
                      <p className="summary mb-0">
                        {t('common.sub_total')}: <span>{doAmountFormat(item.subtotal)}</span>
                      </p>
                      {item.tax_total !== 0 && <p className="summary mb-0">
                        {t('common.tax')}: <span>{doAmountFormat(item.tax_total)}</span>
                      </p>
                      }
                      {item.discount_total !== 0 && <p className="summary mb-0">
                        {t('checkout.discount')}: <span>{doAmountFormat(item.discount_total)}</span>
                      </p>}
                      <p className="summary mb-0">
                        {t('common.total')}: <span>{doAmountFormat(item.total ? item.total : 0)}</span>
                      </p>
                    </td>
                    <td>
                      {item?.created_at ? doDateFormat(item?.created_at) : '-'}<br></br>
                      <small>{doTimeFormat(item?.created_at)}</small>
                    </td>
                    <td>
                      {item?.updated_at ? doDateFormat(item?.updated_at) : '-'}<br></br>
                      <small>{doTimeFormat(item?.updated_at)}</small>
                    </td>
                    {/* <td>
                      <span dangerouslySetInnerHTML={{ __html: doDateFormatWithTime(item?.created_at) }} /></td> */}
                    {/* <td>
                      {doDateFormatWithTime(item?.updated_at)}
                    </td> */}
                    <td className="position-relative">
                      {itemID == item.id && loading.pdf && <span className="loader"></span>}
                      <ul className="list-inline">
                        <li className="list-inline-item">
                          <LinkButton
                            className="link p-0 text-dark"
                            path={`${routes.orders}/${item?.id}`}
                            label=""
                          >
                            <i className="fa-solid fa-eye"></i>
                          </LinkButton>
                        </li>

                        {/* <li className="list-inline-item">
                          <Button
                            type="button"
                            onClick={() => handleOrderDetailsApi('pdf', item.id)}
                            className="link text-dark p-0"
                          >
                            <FontAwesomeIcon icon={faFileInvoice} />
                          </Button>
                        </li> */}

                        {me.client?.enable_receipt_print && <li className="list-inline-item">
                          <Button
                            type="button"
                            // onClick={() => handleOrderDetailsApi('print', item.id)}
                            onClick={() => getOrderDetails(item?.id)}
                            className="link text-dark p-0"
                          >
                            <FontAwesomeIcon icon={faPrint} />
                          </Button>
                        </li>
                        }
                        {/* <li className="list-inline-item">
                        <LinkButton
                          className="link text-dark p-0"
                          path={'#'}
                          label=""
                        >
                          <i className="fa fa-pen"></i>
                        </LinkButton>
                      </li> */}

                        <li className="list-inline-item">
                          <Button type="submit" onClick={() => handleDelete(item.id)} className="transparent p-0">
                            <i className="fa-solid fa-trash text-danger"></i>
                          </Button>
                        </li>

                        {/* <li className="list-inline-item">
                        <LinkButton
                          className="link text-success p-0"
                          path={'#'}
                          label=""
                        >
                          <i className="fa-solid fa-file-waveform"></i>
                        </LinkButton>
                      </li> */}
                      </ul>
                    </td>
                  </tr>
                )
              })}
              {loading.listing === false && orders?.items?.length === 0 && <tr>
                <td colSpan={8} className="text-center no-entry">
                  <aside>
                    <i className="fa-solid fa-ban"></i>
                    {strings.noRecordFound}
                  </aside>
                </td>
              </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
      {/* <div className="d-flex justify-content-center mb-3">
        {loading.listing === true && <span className="loader"></span>}
      </div> */}
      {orders?.pagination?.total_pages > 1 &&
        <Pagination
          current_page={orders?.pagination?.current_page}
          per_page={orders?.pagination?.per_page}
          total_pages={orders?.pagination?.total_pages}
          total_items={orders?.pagination?.total_items}
          onClick={(i: { [key: string]: number }) => {
            setFilterData((prev) => ({
              ...prev,
              page: i.selected + 1,
            })),
              setFilterApply(!filterApply)
          }
          }
        />
      }

      <ReportExport
        title={'Export report orders'}
        type="sales"
      />
      <PrinterAccess />
      <div className="offcanvas offcanvas-end" id="Orderlist" aria-labelledby="OrderlistLabel">
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title" id="OrderlistLabel"><strong>Fillter</strong></h5>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>

        <div className="offcanvas-body">

          <div className="col-12">

            <div className="card-body">
              <form>
                <div className="row">
                  <AsyncSelectField
                    isClearable
                    isMulti
                    cacheOptions
                    label={`${t('customer.select_customers')}`}
                    name="customer_id"
                    placeholder={t('common.choose_option')}
                    loadOptions={loadOption}
                    ref={(ref: any) => (selectAsyncSelect.current = ref)}
                    defaultOptions={customers?.map((item: _Object) => ({
                      label: item?.full_name != '' ? capitalize(item.full_name) : item.mobile_number,
                      value: item.id
                    }))}
                    onChange={(val: any) => {
                      const selectedIds = Array.isArray(val) ? val.map((item: any) => item.value) : [];
                      setFilterData((prev: any) => {
                        const updatedFilter = { ...(prev.filter || {}) };

                        if (!selectedIds.length) {
                          delete updatedFilter.customer_id;
                        } else {
                          updatedFilter.customer_id = selectedIds;
                        }
                        return {
                          ...prev,
                          filter: updatedFilter,
                        };
                      });
                      setFilteredLabel((prev: any) => {
                        const updated = { ...(prev || {}) };
                        if (!selectedIds.length) {
                          delete updated.customer_id;
                          return updated;
                        }
                        updated.customer_id = val.map((x: any) => x.label).join(', ');
                        return updated;
                      });
                    }}
                    value={
                      customers
                        ?.map((item: _Object) => ({ label: item?.full_name != '' ? capitalize(item.full_name) : item.mobile_number, value: item.id }))
                        .filter((opt: any) =>
                          (filterData.filter?.customer_id || []).includes(opt.value)
                        ) || []
                    }
                  />

                  <div className="col-6">
                    <ReactDatePicker
                      label="Start date"
                      selected={filterData?.start_date ? new Date(filterData.start_date) : null}
                      onChange={(date: Date | null) => {
                        setFilterData((prev: any) => {
                          const updated = { ...prev };
                          if (!date) {
                            delete updated.start_date;
                          } else {
                            updated.start_date = doDateFormat(date, 'YYYY-MM-DD');
                          }
                          return updated;
                        });
                        setFilteredLabel((prev: any) => {
                          const updated = { ...prev };

                          if (!date) {
                            delete updated.start_date;
                            return updated;
                          }
                          updated.start_date = doDateFormat(date);
                          return updated;
                        });
                      }}
                      dateFormat="MMM dd, yyyy"
                      placeholderText="Select start date"
                      className="form-control"
                      isClearable
                    />
                  </div>

                  <div className="col-6">
                    <ReactDatePicker
                      label="End date"
                      selected={filterData?.end_date ? new Date(filterData.end_date) : null}
                      onChange={(date: Date | null) => {
                        setFilterData((prev: any) => {
                          const updated = { ...prev };
                          if (!date) {
                            delete updated.end_date;
                          } else {
                            updated.end_date = doDateFormat(date, 'YYYY-MM-DD');
                          }
                          return updated;
                        });
                        setFilteredLabel((prev: any) => {
                          const updated = { ...prev };
                          if (!date) {
                            delete updated.end_date;
                            return updated;
                          }
                          updated.end_date = doDateFormat(date);
                          return updated;
                        });
                      }}
                      dateFormat="MMM dd, yyyy"
                      placeholderText="Select end date"
                      className="form-control"
                      isClearable
                    />
                  </div>

                  <SelectField
                    name="text"
                    label={t('Sort by')}
                    className="col-12"
                    options={[
                      { value: '-created_at', label: 'Created ↓ (Newest)' },
                      { value: 'created_at', label: 'Created ↑ (Oldest)' },
                      { value: '-updated_at', label: 'Updated ↓ (Newest)' },
                      { value: 'updated_at', label: 'Updated ↑ (Oldest)' },
                      { value: 'order_number', label: 'Order number ↑ (Oldest)' },
                      { value: '-order_number', label: 'Order number ↓ (Newest)' },
                    ]}
                    onChange={(e: _Object) => {
                      setFilterData((prev: any) => ({
                        ...prev,
                        sort: e.value,
                      }));
                    }}
                  />
                  <div className="col-12">
                    <Button
                      type="button"
                      onClick={() => setFilterApply(!filterApply)}
                      className="primary mx-auto"
                      data-bs-dismiss="offcanvas" aria-label="Close"
                    >
                      Apply Filters
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setFilterData({
                          page: 1,
                          per_page: process.env.REACT_APP_PER_PAGE,
                          sort: '-created_at',
                          start_date: '',
                          end_date: '',
                          filter: {
                            customer_id: '',
                            payment_status: '',
                          }
                        }),
                          setFilteredLabel({})
                        setFilterApply(!filterApply)
                      }}
                      className="secondry text-danger mx-auto"
                      data-bs-dismiss="offcanvas" aria-label="Close"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div >
      </div >
    </>
  )
}
export default OrdersList
